import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from './useInView'
import {
  MapPin,
  Calendar,
  CreditCard,
  Users,
  Smartphone,
  BarChart3,
  ArrowUpRight,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import spottoMark from '../assets/spotto-mark.png'
import screen01 from '../assets/spotto-screen-01-home.png'
import screen02 from '../assets/spotto-screen-02-explore.png'
import screen03 from '../assets/spotto-screen-03-booking.png'
import screen04 from '../assets/spotto-screen-04-training.png'
import screen05 from '../assets/spotto-screen-05-shop.png'
import { useT } from '../i18n/LanguageContext'
import type { Dictionary } from '../i18n/types'

type ScreenKey = keyof Dictionary['product']['screens']
type CapabilityKey = keyof Dictionary['product']['capabilities']

const screenList: Array<{ key: ScreenKey; src: string }> = [
  { key: 'home', src: screen01 },
  { key: 'explore', src: screen02 },
  { key: 'booking', src: screen03 },
  { key: 'training', src: screen04 },
  { key: 'shop', src: screen05 },
]

const capabilityList: Array<{ key: CapabilityKey; icon: LucideIcon }> = [
  { key: 'search', icon: MapPin },
  { key: 'booking', icon: Calendar },
  { key: 'payments', icon: CreditCard },
  { key: 'trainers', icon: Users },
  { key: 'mobile', icon: Smartphone },
  { key: 'panel', icon: BarChart3 },
]

const AUTO_ADVANCE_MS = 4500

export default function Product() {
  const t = useT()
  const { ref, isInView } = useInView()
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    if (isPaused) return
    const timer = setInterval(() => {
      setActiveIndex((i) => (i + 1) % screenList.length)
    }, AUTO_ADVANCE_MS)
    return () => clearInterval(timer)
  }, [isPaused])

  const activeScreen = screenList[activeIndex]
  const activeLabel = t.product.screens[activeScreen.key]

  return (
    <section id="product" className="relative pt-8 pb-24 min-h-[88vh] flex flex-col justify-center overflow-hidden bg-white/[0.012]">
      <div className="absolute inset-0 bg-radial-top" />

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_220px] gap-10 items-start">
          {/* Left - product info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            {/* Heading — centered within left column */}
            <div className="text-center mb-10">
              <p className="font-mono text-accent-400 text-xs tracking-[0.2em] uppercase mb-5">
                {t.product.eyebrow}
              </p>
              <div className="flex items-center justify-center gap-4">
                <img
                  src={spottoMark}
                  alt=""
                  aria-hidden="true"
                  className="h-14 sm:h-16 w-auto brightness-0 invert opacity-90"
                />
                <h2 className="font-display text-5xl sm:text-6xl font-bold text-white tracking-tight">
                  Spotto
                </h2>
              </div>
            </div>

            <p className="text-lg text-zinc-400 leading-relaxed mb-4">
              {t.product.description1}
            </p>
            <p className="text-zinc-500 leading-relaxed mb-6">
              {t.product.description2}
            </p>

            <div className="grid sm:grid-cols-2 gap-3 max-w-md mx-auto">
              {capabilityList.map((item, i) => {
                const Icon = item.icon
                return (
                  <motion.div
                    key={item.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
                    className="flex items-center gap-3 text-sm text-zinc-300"
                  >
                    <div className="w-8 h-8 rounded-lg border border-accent-500/30 flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-accent-400" />
                    </div>
                    {t.product.capabilities[item.key]}
                  </motion.div>
                )
              })}
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <a
                href="https://play.google.com/store/apps/details?id=pl.spotto.mobile"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] rounded-xl text-sm font-semibold text-white transition-all"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.807 1.626a1 1 0 0 1 0 1.732l-2.807 1.626L15.206 12l2.492-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
                </svg>
                Google Play
              </a>
              <a
                href="https://apps.apple.com/app/spotto"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] rounded-xl text-sm font-semibold text-white transition-all"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                App Store
              </a>
              <a
                href="https://spotto.pl"
                target="_blank"
                rel="noopener noreferrer"
                className="group ml-1 inline-flex items-center gap-1.5 px-2 py-3 text-sm font-medium text-brand-400 hover:text-white transition-colors"
              >
                {t.product.ctaVisit}
                <ArrowUpRight
                  size={14}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </a>
            </div>
          </motion.div>

          {/* Right - phone carousel */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            className="relative flex flex-col items-center lg:self-end"
          >
            <div className="relative">
              {/* Glow behind phone */}
              <div className="absolute inset-0 bg-brand-500/15 rounded-[60px] blur-[80px] scale-75" />

              {/* Phone frame */}
              <div className="relative w-[154px] sm:w-[176px] rounded-[24px] bg-gradient-to-b from-zinc-700 to-zinc-800 p-[2px] shadow-2xl overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={activeScreen.src}
                    src={activeScreen.src}
                    alt={`Spotto: ${activeLabel}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="block w-full rounded-[22px]"
                  />
                </AnimatePresence>
              </div>
            </div>

            {/* Caption */}
            <div className="mt-8 h-5 flex items-center">
              <AnimatePresence mode="wait">
                <motion.p
                  key={activeLabel}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.3 }}
                  className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-400"
                >
                  {activeLabel}
                </motion.p>
              </AnimatePresence>
            </div>

            {/* Dots */}
            <div
              className="mt-4 flex items-center gap-2"
              role="tablist"
              aria-label={t.product.carouselAria}
            >
              {screenList.map((screen, i) => {
                const label = t.product.screens[screen.key]
                return (
                  <button
                    key={screen.key}
                    type="button"
                    role="tab"
                    aria-selected={i === activeIndex}
                    aria-label={label}
                    onClick={() => setActiveIndex(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === activeIndex
                        ? 'w-6 bg-brand-400'
                        : 'w-1.5 bg-zinc-600 hover:bg-zinc-500'
                    }`}
                  />
                )
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
